// App-wide state: the authed user. React Context, no Redux.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { api, clearToken, getToken, setToken } from '@/lib/api';
import { SUPPORTED_LANG_CODES } from '@/lib/lang';

const AppContext = createContext(null);

const DEMO_PERSONA_KEY = 'demo_persona';
const DEMO_ORG_ID_KEY = 'demo_persona_org_id';
const DEMO_ORG_NAME_KEY = 'demo_persona_org_name';
const LANG_KEY = 'pref_lang';
// "View as this center": we stash the real platform-admin token so we can
// restore it on "Return to platform view". The active JWT in `portal_token`
// is swapped to the org_admin scoped token while impersonation is on.
const REAL_TOKEN_KEY = 'portal_token_real';
const IMP_META_KEY = 'impersonation_meta';

const readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLS = (k, v) => {
  try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* ignore */ }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Which exam the sidebar nav targets. On exam-scoped pages the URL drives it; on
  // the exam-agnostic dashboard it's a remembered preference. Persisted so a switch
  // survives a reload.
  const [activeExam, setActiveExamState] = useState(() => readLS('active_exam'));
  const setActiveExam = useCallback((e) => {
    setActiveExamState(e);
    writeLS('active_exam', e);
  }, []);

  // Owner-only demo persona toggle (the platform admin flips into a center-admin
  // view for Zoom walkthroughs so they never expose real org-admin data). Pure
  // UI flip + an X-Demo-Org-Id header so org-scoped APIs return the demo org.
  const [demoPersona, setDemoPersonaState] = useState(() => readLS(DEMO_PERSONA_KEY));
  const [demoOrgId, setDemoOrgIdState] = useState(() => readLS(DEMO_ORG_ID_KEY));
  const [demoOrgName, setDemoOrgNameState] = useState(() => readLS(DEMO_ORG_NAME_KEY));

  const setDemoPersona = useCallback((persona, orgId, orgName) => {
    setDemoPersonaState(persona);
    setDemoOrgIdState(orgId || null);
    setDemoOrgNameState(orgName || null);
    writeLS(DEMO_PERSONA_KEY, persona);
    writeLS(DEMO_ORG_ID_KEY, orgId || null);
    writeLS(DEMO_ORG_NAME_KEY, orgName || null);
  }, []);

  const clearDemoPersona = useCallback(() => {
    setDemoPersonaState(null);
    setDemoOrgIdState(null);
    setDemoOrgNameState(null);
    writeLS(DEMO_PERSONA_KEY, null);
    writeLS(DEMO_ORG_ID_KEY, null);
    writeLS(DEMO_ORG_NAME_KEY, null);
  }, []);

  // Instructional language for lessons + walkthroughs. Persisted so the choice
  // survives reload. Only tutor-text is translated; exam content stays English.
  const [lang, setLangState] = useState(() => {
    const stored = readLS(LANG_KEY);
    return SUPPORTED_LANG_CODES.includes(stored) ? stored : 'en';
  });
  const setLang = useCallback((code) => {
    const next = SUPPORTED_LANG_CODES.includes(code) ? code : 'en';
    setLangState(next);
    writeLS(LANG_KEY, next);
  }, []);

  // "View as this center" impersonation state. Persisted so a page reload
  // does not silently drop the platform admin back into their own view mid
  // session (which would look like a bug). Meta holds org_name / target
  // email / expiry — enough for the banner without a round-trip.
  const [impersonation, setImpersonationState] = useState(() => {
    try { const raw = readLS(IMP_META_KEY); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });

  const startImpersonation = useCallback(async (orgId, userId = null) => {
    // Stash the real platform-admin token BEFORE we swap it, so "Return to
    // platform view" can always restore it (even after a hard reload).
    const realToken = getToken();
    if (realToken) writeLS(REAL_TOKEN_KEY, realToken);
    const res = await api.adminImpersonateStart(orgId, userId);
    const { token, expires_at, event_id, org, target } = res.data;
    setToken(token);
    const meta = { org_id: org.org_id, org_name: org.name,
                   target_email: target.email, target_name: target.name,
                   target_role: target.role, expires_at, event_id };
    writeLS(IMP_META_KEY, JSON.stringify(meta));
    setImpersonationState(meta);
    // Refresh /auth/me so the entire app re-renders as the target user.
    const me = await api.me();
    setUser(me.data.user);
    return meta;
  }, []);

  const stopImpersonation = useCallback(async () => {
    // Fire-and-forget the audit close; the client's guarantee is restoring
    // the real token, not the server-side log write.
    try { await api.adminImpersonateStop(); } catch { /* token may have expired */ }
    const realToken = readLS(REAL_TOKEN_KEY);
    if (realToken) setToken(realToken); else clearToken();
    writeLS(REAL_TOKEN_KEY, null);
    writeLS(IMP_META_KEY, null);
    setImpersonationState(null);
    try {
      const me = await api.me();
      setUser(me.data.user);
    } catch {
      // Real token invalid → sign out cleanly.
      clearToken();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const boot = async () => {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.me();
        setUser(res.data.user);
      } catch {
        clearToken();
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  const signIn = useCallback((token, userData) => {
    setToken(token);
    setUser(userData);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    clearDemoPersona();
    // Also nuke any impersonation shadow — otherwise a stale real-token would
    // linger in localStorage after logout, which is a real security foot-gun.
    writeLS(REAL_TOKEN_KEY, null);
    writeLS(IMP_META_KEY, null);
    setImpersonationState(null);
    setUser(null);
  }, [clearDemoPersona]);

  // Effective role drives all sidebar / navigation gating. Real role is preserved
  // on `user.role` so the persona switch itself can stay platform_admin-only.
  const effectiveRole = (demoPersona && user?.role === 'platform_admin') ? demoPersona : user?.role;
  // Badge visibility is driven by the backend (auth._attach_badge honours the
  // org's `provisioned_by_enabled` flag), so we just trust whatever /auth/me
  // returned after the last toggle. Never fall back to a cached org name —
  // that would show the badge even when the center has it switched off.
  const effectiveProvisionedBy = user?.provisioned_by || null;

  return (
    <AppContext.Provider value={{
      user, setUser, loading, signIn, signOut,
      activeExam, setActiveExam,
      demoPersona, demoOrgId, demoOrgName,
      setDemoPersona, clearDemoPersona,
      effectiveRole, effectiveProvisionedBy,
      lang, setLang,
      impersonation, startImpersonation, stopImpersonation,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
